import ENDPOINTS from 'tradera-constants/endpoints';
import {
    axiosWithTokenRefresh,
    axiosConfigs,
    utilizeCancelToken,
    finalizeResponse,
    handleError,
    API_ERRORS
} from 'tradera-utils/api';
import { toLocalizedUrl } from 'tradera-utils/url';
import logger from 'packages/logger';

export const fetchDescription = (
    itemId,
    languageCodeIso2,
    fromLanguageCodeIso2
) => {
    const { cancel, cancelToken } = utilizeCancelToken(
        'getItemDescriptionTranslated'
    );

    if (cancel) cancel();
    return axiosWithTokenRefresh()
        .get(ENDPOINTS.ITEM_DESCRIPTION(itemId), {
            cancelToken,
            ...axiosConfigs.authenticated,
            params: {
                languageCodeIso2,
                fromLanguageCodeIso2
            }
        })
        .then(finalizeResponse())
        .catch(handleError());
};

/**
 * Returns an object with bid information for the item with the given ID.
 * @param {*} itemId
 */
export const getItemApi = async (itemId, preferredLanguageCode) => {
    return axiosWithTokenRefresh()
        .get(toLocalizedUrl(`/item/${itemId}.json`, preferredLanguageCode))
        .then(finalizeResponse())
        .then(({ data, status }) => {
            // Normalize & format API response data
            // if (data) {
            //     data = normalizeItemModel(data);
            // }
            return { data, status };
        })
        .catch(
            handleError({
                // Ignore 400 & 401 errors that slip through token refresh
                400: API_ERRORS.IGNORE_ME,
                401: API_ERRORS.IGNORE_ME
            })
        );
};

/**
 * Places a bid for the given auction
 * @param {*} itemId
 * @param {*} amount
 */
export const placeBidApi = async (
    itemId,
    amount,
    onLoggedOut,
    preferredLanguageCode
) => {
    try {
        const response = await axiosWithTokenRefresh().post(
            toLocalizedUrl(`/item/place-bid/${itemId}`, preferredLanguageCode),
            { amount },
            axiosConfigs.authenticated
        );
        const {
            data: {
                bid,
                item,
                transactionTotal,
                potentialBidChange,
                isMaxBid,
                weightedBidValue,
                serverSecondsLeft
            }
        } = response;

        const {
            bidId,
            isSuccessful,
            leadingBidAmount,
            leadingBidderMemberId,
            leadingMemberAlias: leadingBidMemberAlias,
            nextBidAmount: nextValidBidAmount,
            validationResult,
            placedBid
        } = bid;

        const {
            bidCount,
            categoryId,
            endDate,
            itemType,
            openingBid,
            reservePriceReached,
            sellermemberId: sellerMemberId,
            startDate
        } = item;

        return {
            isSuccessful,
            validationResult,
            item: {
                bidCount,
                categoryId,
                endDate,
                itemId: item.itemId,
                itemType,
                openingBid,
                reservePriceReached,
                sellerMemberId,
                startDate
            },
            bidInfo: {
                bidCount,
                bidId,
                isMaxBid,
                leadingBidAmount,
                leadingBidderMemberId,
                leadingBidMemberAlias,
                nextValidBidAmount,
                potentialBidChange,
                reservePriceReached,
                transactionTotal,
                weightedBidValue
            },
            analyticsData: {
                bidId,
                transactionTotal,
                potentialBidChange,
                isMaxBid,
                weightedBidValue
            },
            myMaxBidAmount: placedBid,
            serverSecondsLeft
        };
    } catch (error) {
        if (error.message === API_ERRORS.LOGGED_OUT) {
            onLoggedOut();
        } else {
            logger(error);
        }
    }
};

export const getItemsFromSeller = (params, preferredLanguageCode) => {
    return axiosWithTokenRefresh()
        .get(
            toLocalizedUrl(
                '/api/webapi/discover/more-from-seller/',
                preferredLanguageCode
            ),
            { params }
        )
        .then(finalizeResponse())
        .catch(handleError());
};

export const getCombinedShippingUpsell = params => {
    return axiosWithTokenRefresh()
        .post(
            ENDPOINTS.COMBINED_SHIPPING_UPSELL,
            params,
            axiosConfigs.authenticated
        )
        .then(finalizeResponse())
        .catch(handleError());
};
